import * as THREE from "three";
import { defineModel, type Part } from "../../lib";

// A coal-fired power station: a long boxy turbine hall, a tall banded chimney
// trailing a wisp of smoke, and a small concrete cooling tower beside it. The
// industrial silhouette of fossil generation, in the classic flat-colour look.
const HALL = "#b3b8be";
const ROOF = "#73787e";
const STACK = "#cdc8be";
const BAND = "#c4392f";
const COOL = "#cfcabb";
const SMOKE = "#9aa0a6";

export const coalPowerPlant = defineModel({
  id: "coal-power-plant",
  batchId: "electric",
  name: "Coal power plant",
  tags: ["building", "electric", "power", "energy", "coal", "plant", "fossil"],
  summary: "A coal-fired turbine hall with a banded chimney and cooling tower.",
  footprint: 1.0,
  parts(): Part[] {
    return [
      { geometry: new THREE.BoxGeometry(2.0, 0.12, 1.4), color: "#6f6f74", position: [0, 0.06, 0] },
      // Turbine hall (wide, low), front-left.
      { geometry: new THREE.BoxGeometry(1.0, 0.56, 0.78), color: HALL, position: [-0.5, 0.4, 0.12] },
      { geometry: new THREE.BoxGeometry(1.04, 0.08, 0.82), color: ROOF, position: [-0.5, 0.72, 0.12] },
      { geometry: new THREE.BoxGeometry(0.84, 0.16, 0.02), color: "#3a4a55", position: [-0.5, 0.4, 0.52] },
      { geometry: new THREE.BoxGeometry(0.16, 0.3, 0.03), color: "#f2c14e", position: [-0.84, 0.22, 0.52] },
      // Boiler house, taller, centre.
      { geometry: new THREE.BoxGeometry(0.56, 1.0, 0.66), color: HALL, position: [0.1, 0.62, -0.02] },
      { geometry: new THREE.BoxGeometry(0.6, 0.08, 0.7), color: ROOF, position: [0.1, 1.16, -0.02] },
      // Banded chimney, right, with protruding red bands and an offset smoke wisp.
      { geometry: new THREE.CylinderGeometry(0.1, 0.14, 1.8, 12), color: STACK, position: [0.66, 0.9, -0.28] },
      { geometry: new THREE.CylinderGeometry(0.13, 0.13, 0.14, 12), color: BAND, position: [0.66, 1.5, -0.28] },
      { geometry: new THREE.CylinderGeometry(0.115, 0.115, 0.14, 12), color: BAND, position: [0.66, 1.74, -0.28] },
      { geometry: new THREE.SphereGeometry(0.13, 8, 6), color: SMOKE, position: [0.66, 1.92, -0.28], scale: [1.1, 0.7, 1.1] },
      { geometry: new THREE.SphereGeometry(0.11, 8, 6), color: "#a8aeb4", position: [0.78, 2.08, -0.34], scale: [1, 0.75, 1] },
      { geometry: new THREE.SphereGeometry(0.08, 7, 5), color: "#bcc2c8", position: [0.9, 2.22, -0.4] },
      // Cooling tower: a clearly separate hyperboloid, front-right, with a small
      // steam wisp so it does not read as a dome on a box.
      { geometry: new THREE.CylinderGeometry(0.22, 0.34, 0.46, 14), color: COOL, position: [0.7, 0.35, 0.42] },
      { geometry: new THREE.CylinderGeometry(0.3, 0.22, 0.34, 14), color: COOL, position: [0.7, 0.73, 0.42] },
      { geometry: new THREE.CylinderGeometry(0.27, 0.27, 0.04, 14), color: "#3a3a3e", position: [0.7, 0.91, 0.42] },
      { geometry: new THREE.SphereGeometry(0.24, 9, 6), color: "#eef0f2", position: [0.7, 1.02, 0.42], scale: [1, 0.5, 1] },
      { geometry: new THREE.SphereGeometry(0.15, 8, 6), color: "#fafbfc", position: [0.66, 1.18, 0.45] },
      // A small coal pile for life.
      { geometry: new THREE.SphereGeometry(0.16, 7, 5), color: "#2b2b30", position: [-0.5, 0.12, 0.62], scale: [1.3, 0.5, 1] },
    ];
  },
  animations: [],
});
